<template>
  <div class="show_all_content_wrapper">
    <!-- Start:: Main Section -->
    <main>
      <!--  =========== Start:: Filter Form =========== -->
      <div
        class="filter_content_wrapper"
        :class="{ active: filterFormIsActive }"
      >
        <button
          class="filter_toggler"
          @click="filterFormIsActive = !filterFormIsActive"
        >
          <i class="fal fa-times"></i>
        </button>
        <div class="filter_title_wrapper">
          <h5>{{ $t("TITLES.searchBy") }}</h5>
        </div>
        <div class="filter_form_wrapper">
          <form @submit.prevent="submitFilterForm">
            <div class="row justify-content-center align-items-center w-100">
              <!-- Start:: Order Number Input -->
              <base-input
                col="3"
                type="text"
                :placeholder="$t('PLACEHOLDERS.orderNum')"
                v-model.trim="filterOptions.orderNum"
                :maxlength="100"
                @input="limitInputLength('orderNum', $event, 100)"
              />
              <!-- End:: Order Number Input -->

              <!-- Start:: User Name Input -->
              <base-input
                col="3"
                type="text"
                :placeholder="$t('PLACEHOLDERS.clientName')"
                v-model.trim="filterOptions.userName"
                :maxlength="100"
                @input="limitInputLength('userName', $event, 100)"
              />
              <!-- End:: User Name Input -->

              <!-- Start:: Client Number Input -->
              <base-input
                col="3"
                type="text"
                :placeholder="$t('PLACEHOLDERS.clientNumber')"
                v-model.trim="filterOptions.clientNumber"
                :maxlength="50"
                @input="limitInputLength('clientNumber', $event, 50)"
              />
              <!-- End:: Client Number Input -->

              <!-- Start:: Region Input -->
              <base-select-input
                col="3"
                :optionsList="regions"
                :placeholder="$t('PLACEHOLDERS.region')"
                v-model="filterOptions.region"
              />
              <!-- End:: Region Input -->

              <!-- Start:: City Input -->
              <base-select-input
                col="3"
                :optionsList="cities"
                :placeholder="$t('PLACEHOLDERS.city')"
                v-model="filterOptions.city"
                :disabled="!filterOptions.region"
              />
              <!-- End:: City Input -->

              <!-- Start:: District Input -->
              <base-select-input
                col="3"
                :optionsList="districts"
                :placeholder="$t('PLACEHOLDERS.district')"
                v-model="filterOptions.district"
                :disabled="!filterOptions.city"
              />
              <!-- End:: District Input -->

              <!-- Start:: Status Input -->
              <base-select-input
                col="3"
                :optionsList="activeStatuses"
                :placeholder="$t('PLACEHOLDERS.orderStatus')"
                v-model="filterOptions.status"
              />
              <!-- End:: Status Input -->

              <!-- Start:: Start Date Input -->
              <base-picker-input
                col="3"
                type="date"
                :placeholder="$t('PLACEHOLDERS.startDate')"
                v-model.trim="filterOptions.from_date"
              />
              <!-- End:: Start Date Input -->

              <!-- Start:: End Date Input -->
              <base-picker-input
                col="3"
                type="date"
                :placeholder="$t('PLACEHOLDERS.endDate')"
                v-model.trim="filterOptions.to_date"
              />
              <!-- End:: End Date Input -->
            </div>

            <div class="btns_wrapper">
              <a-tooltip placement="bottom">
                <template slot="title">
                  <span>{{ $t("BUTTONS.search") }}</span>
                </template>
                <span
                  class="submit_btn"
                  @click="submitFilterForm"
                  :disabled="isWaitingRequest"
                >
                  <i class="fal fa-search"></i>
                </span>
              </a-tooltip>

              <a-tooltip placement="bottom">
                <template slot="title">
                  <span>{{ $t("BUTTONS.rseet_search") }}</span>
                </template>
                <span
                  class="reset_btn"
                  :disabled="isWaitingRequest"
                  @click="resetFilter"
                >
                  <i class="fal fa-redo"></i>
                </span>
              </a-tooltip>
            </div>
          </form>
        </div>
      </div>
      <!--  =========== End:: Filter Form =========== -->

      <!--  =========== Start:: Table Title =========== -->
      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>{{ $t("PLACEHOLDERS.orders") }}</h5>
          <button
            v-if="!filterFormIsActive"
            class="filter_toggler"
            @click.stop="filterFormIsActive = !filterFormIsActive"
          >
            <i class="fal fa-search"></i>
          </button>
        </div>
      </div>
      <!--  =========== End:: Table Title =========== -->

      <!--  =========== Start:: Data Table =========== -->
      <v-data-table
        :key="tableKey"
        class="thumb"
        :loading="loading"
        :loading-text="$t('TABLES.loadingData')"
        :search="searchValue"
        :headers="tableHeaders"
        :items="tableRows"
        item-class="ltr"
        :items-per-page="paginations.items_per_page"
        hide-default-footer
      >
        <!-- Start:: No Data State -->
        <template v-slot:no-data>
          {{ $t("TABLES.noData") }}
        </template>
        <!-- Start:: No Data State -->

        <template v-slot:[`item.id`]="{ item, index }">
          <p class="blue-grey--text text--darken-1 fs-3" v-if="!item.id">-</p>
          <p v-else>
            {{
              (paginations.current_page - 1) * paginations.items_per_page +
              index +
              1
            }}
          </p>
        </template>

        <!-- <template v-slot:[`item.id`]="{ item }">
          <h6 class="text-danger" v-if="!item.id">
            {{ $t("TABLES.noData") }}
          </h6>
          <h6 v-else>{{ item.id }}</h6>
        </template> -->

        <!-- <template v-slot:[`item.serialNumber`]="{ item }">
          <p class="blue-grey--text text--darken-1 fs-3" v-if="!item.serialNumber">-</p>
          <p v-else>{{ item.serialNumber }}</p>
        </template> -->

        <!-- Start:: Order Status -->
        <template v-slot:[`item.status`]="{ item }">
          <v-chip>
            <!-- {{ item.status }} -->
            {{ getStatusName(item.status) }}
          </v-chip>
        </template>
        <!-- End:: Order Status -->

        <!-- Start:: Average Rating -->
        <template v-slot:[`item.average_rating`]="{ item }">
          <div class="d-flex justify-center align-center">
            <rating-preview
              :rate="item.average_rating || 0"
              :size="16"
              :length="5"
            />
          </div>
        </template>
        <!-- End:: Average Rating -->

        <!-- Start:: Order Number -->
        <template v-slot:[`item.order_number`]="{ item }">
          <p v-if="item.order_number">{{ item.order_number }}</p>
          <p class="blue-grey--text text--darken-1 fs-3" v-else>-</p>
        </template>
        <!-- End:: Order Number -->

        <!-- Start:: User Name -->
        <template v-slot:[`item.user_name`]="{ item }">
          <p v-if="item.user?.name">{{ item.user.name }}</p>
          <p class="blue-grey--text text--darken-1 fs-3" v-else>-</p>
        </template>
        <!-- End:: User Name -->

        <!-- Start:: City -->
        <template v-slot:[`item.city`]="{ item }">
          <p v-if="item.address?.city">{{ item.address.city?.name }}</p>
          <p class="blue-grey--text text--darken-1 fs-3" v-else>-</p>
        </template>
        <!-- End:: City -->

        <!-- Start:: District -->
        <template v-slot:[`item.district`]="{ item }">
          <p v-if="item.address?.district">{{ item.address.district?.name }}</p>
          <p class="blue-grey--text text--darken-1 fs-3" v-else>-</p>
        </template>
        <!-- End:: District -->

        <!-- Start:: Total -->
        <template v-slot:[`item.total`]="{ item }">
          <p v-if="item.total">{{ item.total }}</p>
          <p class="blue-grey--text text--darken-1 fs-3" v-else>-</p>
        </template>
        <!-- End:: Total -->

        <!-- Start:: Created At -->
        <template v-slot:[`item.created_at`]="{ item }">
          <p v-if="item.created_at">{{ formatDate(item.created_at) }}</p>
          <p class="blue-grey--text text--darken-1 fs-3" v-else>-</p>
        </template>
        <!-- End:: Created At -->

        <!-- Start:: Actions -->
        <template v-slot:[`item.actions`]="{ item }">
          <div class="actions">
            <!-- Start:: Accept Order Button (only for 'new' status) -->
            <a-tooltip placement="bottom" v-if="item.status === 'new'">
              <template slot="title">
                <span>{{ $t("BUTTONS.acceptOrder") }}</span>
              </template>
              <!-- {{ item.status }} -->
              <button
                class="btn_accept"
                @click.stop="acceptOrder(item)"
                :disabled="isProcessingOrder[item.id]"
              >
                <i class="fal fa-check"></i>
              </button>
            </a-tooltip>
            <!-- End:: Accept Order Button -->

            <!-- Start:: Reject Order Button (only for 'new' status) -->
            <a-tooltip placement="bottom" v-if="item.status === 'new'">
              <template slot="title">
                <span>{{ $t("BUTTONS.rejectOrder") }}</span>
              </template>
              <button
                class="btn_reject"
                @click.stop="rejectOrder(item)"
                :disabled="isProcessingOrder[item.id]"
              >
                <i class="fal fa-times"></i>
              </button>
            </a-tooltip>
            <!-- End:: Reject Order Button -->

            <a-tooltip placement="bottom">
              <template slot="title">
                <span>{{ $t("BUTTONS.show") }}</span>
              </template>
              <button class="btn_show" @click.stop="showItem(item)">
                <i class="fal fa-eye"></i>
              </button>
            </a-tooltip>

            <a-tooltip
              placement="bottom"
              v-if="item.status !== 'cancelled' && item.status !== 'rejected'"
            >
              <template slot="title" class="hover_tooltip">
                <span>{{ $t("PLACEHOLDERS.electronicInvoice") }}</span>
              </template>
              <button
                class="btn_show"
                @click.stop="downloadInvoice(item)"
                :disabled="isDownloadingInvoice[item.id]"
              >
                <i class="fas fa-file-pdf"></i>
              </button>
            </a-tooltip>

            <!-- Start:: Update Status Button (only for 'new' status) -->
            <a-tooltip
              placement="bottom"
              v-if="item.status === 'ready_for_delivery'"
            >
              <template slot="title">
                <span>{{ $t("BUTTONS.updateStatus") }}</span>
              </template>
              <button
                class="btn_processing"
                @click.stop="submitStatusUpdate(item)"
              >
                <i class="fal fa-tasks"></i>
              </button>
            </a-tooltip>
            <!-- End:: Update Status Button -->

            <!-- Start:: Change Status Button (only for 'processing' status) -->
            <!-- {{ item.status }} -->
            <button
              v-if="item.status === 'processing'"
              class="btn_change_status"
              :title="$t('BUTTONS.changeStatus')"
              @click.stop="changeOrderStatus(item)"
              :disabled="isProcessingOrder[item.id]"
            >
              <i class="fal fa-sync-alt"></i>
            </button>
            <!-- End:: Change Status Button -->
          </div>
        </template>
        <!-- End:: Actions -->

        <!-- ======================== Start:: Dialogs ======================== -->
        <template v-slot:top>
          <!-- Start:: Replay Modal -->
          <description-modal
            v-if="dialogReplay"
            :modalIsOpen="dialogReplay"
            :modalDesc="selectedReplayTextToShow"
            @toggleModal="dialogReplay = !dialogReplay"
          />
          <!-- End:: Replay Modal -->

          <!-- Start:: Update Status Modal -->
          <v-dialog v-model="dialogUpdateStatus" max-width="600px" persistent>
            <v-card>
              <h3 class="text-h5">
                {{ $t("BUTTONS.updateStatus") }}
              </h3>
              <v-card-text class="pt-6">
                <v-select
                  v-model="selectedStatus"
                  :items="activeStatuses"
                  item-text="name"
                  item-value="value"
                  :label="$t('PLACEHOLDERS.selectStatus')"
                  outlined
                  dense
                  return-object
                  :menu-props="{ offsetY: true }"
                ></v-select>
              </v-card-text>
              <v-card-actions class="px-4 pb-4">
                <v-spacer></v-spacer>
                <v-btn
                  text
                  @click="closeStatusModal"
                  :disabled="isProcessingOrder[selectedOrderId]"
                >
                  {{ $t("BUTTONS.cancel") }}
                </v-btn>
                <v-btn
                  color="primary"
                  @click="submitStatusUpdate"
                  :disabled="
                    !selectedStatus || isProcessingOrder[selectedOrderId]
                  "
                  :loading="isProcessingOrder[selectedOrderId]"
                >
                  {{ $t("BUTTONS.save") }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <!-- End:: Update Status Modal -->
        </template>
        <!-- ======================== End:: Dialogs ======================== -->
      </v-data-table>
      <!--  =========== End:: Data Table =========== -->
    </main>
    <!-- End:: Main Section -->

    <!-- Start:: Pagination -->
    <template>
      <div class="pagination_container text-center mt-3 mb-0">
        <v-pagination
          class="py-0"
          square
          v-model="paginations.current_page"
          :length="paginations.last_page"
          :total-visible="6"
          @input="updateRouterQueryParam($event)"
          :prev-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-right' : 'fal fa-angle-left'
          "
          :next-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-left' : 'fal fa-angle-right'
          "
        />
      </div>
    </template>
    <!-- End:: Pagination -->
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import RatingPreview from "@/components/ui/RatingPreview.vue";

export default {
  name: "AllRegions",
  components: {
    RatingPreview,
  },
  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),
    activeStatuses() {
      return [
        {
          id: 1,
          name: this.$t("PLACEHOLDERS.new"),
          value: "new",
        },
        {
          id: 2,
          name: this.$t("PLACEHOLDERS.processing"),
          value: "processing",
        },
        {
          id: 3,
          name: this.$t("PLACEHOLDERS.ready_for_delivery"),
          value: "ready_for_delivery",
        },
        {
          id: 4,
          name: this.$t("PLACEHOLDERS.on_the_way"),
          value: "on_the_way",
        },
        {
          id: 5,
          name: this.$t("PLACEHOLDERS.delivered"),
          value: "delivered",
        },
        {
          id: 6,
          name: this.$t("PLACEHOLDERS.cancelled"),
          value: "cancelled",
        },
      ];
    },
    userType() {
      return [
        {
          id: 1,
          name: this.$t("PLACEHOLDERS.moder"),
          value: "manager",
        },
        {
          id: 2,
          name: this.$t("PLACEHOLDERS.impular"),
          value: "employee",
        },
      ];
    },
  },

  data() {
    return {
      // Start:: Loading Data
      loading: false,
      isWaitingRequest: false,
      // End:: Loading Data

      // Start:: Filter Data
      filterFormIsActive: false,
      filterOptions: {
        orderNum: null,
        userName: null,
        clientNumber: null,
        region: null,
        city: null,
        district: null,
        status: null,
        from_date: null,
        to_date: null,
      },
      // End:: Filter Data

      // Start:: Table Data
      searchValue: "",
      tableHeaders: [
        {
          text: this.$t("TABLES.Clients.serialNumber"),
          value: "id",
          align: "center",
          width: "80",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.orderNum"),
          value: "order_number",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.clientName"),
          value: "user_name",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.city"),
          value: "city",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.district"),
          value: "district",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("TABLES.subscriptions.Totalorder"),
          value: "total",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.orderStatus"),
          value: "status",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.average_rating"),
          value: "average_rating",
          sortable: false,
          align: "center",
          width: "150",
        },
        {
          text: this.$t("PLACEHOLDERS.created_at"),
          value: "created_at",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("TABLES.Clients.actions"),
          value: "actions",
          sortable: false,
          align: "center",
        },
      ],
      tableRows: [],
      tableKey: 0,
      today: new Date(),
      // End:: Table Data

      // Start:: Cities & Districts Data
      regions: [],
      cities: [],
      districts: [],
      // End:: Cities & Districts Data

      // Start:: Pagination Data
      paginations: {
        current_page: 1,
        last_page: 1,
        items_per_page: 6,
      },
      // End:: Pagination Data

      // Start:: Dialogs Control Data
      dialogReplay: false,
      selectedReplayTextToShow: "",
      dialogUpdateStatus: false,
      selectedStatus: null,
      selectedOrderId: null,
      // End:: Dialogs Control Data

      // Start:: Download/Processing States
      isDownloadingInvoice: {},
      isProcessingOrder: {},
      // End:: Download/Processing States
    };
  },

  watch: {
    // Start:: Page Query Param Watcher To Get Page Data Based On It's Change
    ["$route.query.page"]() {
      this.paginations.current_page = +this.$route.query.page;
      this.setTableRows();
    },
    // End:: Page Query Param Watcher To Get Page Data Based On It's Change
    // Start:: Watch Region To Load Cities And Reset City
    "filterOptions.region"(newRegion) {
      this.filterOptions.city = null;
      this.filterOptions.district = null;
      this.cities = [];
      this.districts = [];
      if (newRegion && newRegion.id) {
        this.getCitiesByRegion(newRegion.id);
      }
    },
    // End:: Watch Region To Load Cities And Reset City
    // Start:: Watch City To Load Districts And Reset District
    "filterOptions.city"(newCity) {
      this.filterOptions.district = null;
      this.districts = [];
      if (newCity && newCity.id) {
        this.getDistrictsByCity(newCity.id);
      }
    },
    // End:: Watch City To Load Districts And Reset District
  },

  methods: {
    limitInputLength(field, event, maxLength) {
      const value = event.target ? event.target.value : event;
      if (value && value.length > maxLength) {
        const limitedValue = value.slice(0, maxLength);
        this.filterOptions[field] = limitedValue;
        if (event.target) {
          event.target.value = limitedValue;
        }
      }
    },
    getStatusName(status) {
      const statusMap = {
        new: this.$t("PLACEHOLDERS.new"),
        processing: this.$t("PLACEHOLDERS.processing"),
        ready_for_delivery: this.$t("PLACEHOLDERS.ready_for_delivery"),
        on_the_way: this.$t("PLACEHOLDERS.on_the_way"),
        delivered: this.$t("PLACEHOLDERS.delivered"),
        cancelled: this.$t("PLACEHOLDERS.cancelled"),
      };
      return statusMap[status] || status;
    },
    formatDate(dateString) {
      if (!dateString) return "-";
      const date = new Date(dateString);
      const pad = (n) => (n < 10 ? `0${n}` : n);
      const year = date.getFullYear();
      const month = pad(date.getMonth() + 1);
      const day = pad(date.getDate());
      const hours = pad(date.getHours());
      const minutes = pad(date.getMinutes());
      const seconds = pad(date.getSeconds());
      return `${year}-${month}-${day}, ${hours}:${minutes}:${seconds}`;
    },
    async downloadInvoice(item) {
      // Set downloading state
      this.$set(this.isDownloadingInvoice, item.id, true);

      try {
        const response = await this.$axios({
          method: "GET",
          url: `invoices/order/${item.id}/download`,
          responseType: "blob",
        });

        // Create a blob from the response
        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `invoice_${item.order_number || item.id}.pdf`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        this.$message.success(this.$t("MESSAGES.downloadSuccess"));
      } catch (error) {
        console.log(error.response?.data?.message || error.message);
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.downloadError")
        );
      } finally {
        // Reset downloading state after a short delay to ensure download completes
        setTimeout(() => {
          this.$set(this.isDownloadingInvoice, item.id, false);
        }, 1000);
      }
    },

    // Start:: Accept Order
    async acceptOrder(item) {
      this.$set(this.isProcessingOrder, item.id, true);
      try {
        await this.$axios({
          method: "PATCH",
          url: `orders/${item.id}/status`,
          data: { status: "processing" },
        });

        this.$message.success(this.$t("MESSAGES.orderAccepted"));

        // ✅ REFRESH DATA FROM API
        await this.setTableRows();
        this.tableKey++;
      } catch (error) {
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.error")
        );
      } finally {
        this.$set(this.isProcessingOrder, item.id, false);
      }
    },

    // End:: Accept Order

    // Start:: Status Modal Methods
    openStatusModal(item) {
      this.selectedOrderId = item.id;
      this.selectedStatus = null;
      this.dialogUpdateStatus = true;
    },
    closeStatusModal() {
      this.dialogUpdateStatus = false;
      this.selectedStatus = null;
      this.selectedOrderId = null;
    },
    async submitStatusUpdate(item) {
      // if (!this.selectedStatus || !this.selectedOrderId) return;

      // this.$set(this.isProcessingOrder, this.selectedOrderId, true);
      try {
        const response = await this.$axios({
          method: "PATCH",
          url: `orders/${item.id}/status`,
          data: {
            status: "delivered",
          },
        });

        this.setTableRows();
        // Update item status locally for immediate UI update
        // const itemIndex = this.tableRows.findIndex(
        //   (row) => row.id === this.selectedOrderId
        // );
        // if (itemIndex !== -1) {
        //   this.$set(
        //     this.tableRows[itemIndex],
        //     "status",
        //     this.selectedStatus.value
        //   );
        // }

        this.$message.success(response.data.message);
        this.closeStatusModal();
        // this.setTableRows();
      } catch (error) {
        console.log(error.response?.data?.message || error.message);
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.error")
        );
      } finally {
        // this.$set(this.isProcessingOrder, this.selectedOrderId, false);
      }
    },
    // End:: Status Modal Methods

    // Start:: Reject Order
    async rejectOrder(item) {
      this.$set(this.isProcessingOrder, item.id, true);
      try {
        await this.$axios({
          method: "PATCH",
          url: `orders/${item.id}/status`,
          data: {
            status: "cancelled",
          },
        });

        this.$message.success(this.$t("MESSAGES.orderRejected"));

        // Refetch table data and force re-render
        await this.setTableRows();
        this.tableKey++;
      } catch (error) {
        console.log(error.response?.data?.message || error.message);
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.error")
        );
      } finally {
        this.$set(this.isProcessingOrder, item.id, false);
      }
    },
    // End:: Reject Order

    // Start:: Change Order Status
    async changeOrderStatus(item) {
      this.$set(this.isProcessingOrder, item.id, true);
      try {
        await this.$axios({
          method: "PATCH",
          url: `orders/${item.id}/status`,
          data: {
            status: "ready_for_delivery",
          },
        });

        this.$message.success(this.$t("MESSAGES.statusChanged"));

        // Refetch table data and force re-render
        await this.setTableRows();
        this.tableKey++;
      } catch (error) {
        console.log(error.response?.data?.message || error.message);
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.error")
        );
      } finally {
        this.$set(this.isProcessingOrder, item.id, false);
      }
    },
    // End:: Change Order Status

    // Start:: Handel Filter
    async submitFilterForm() {
      if (this.$route.query.page !== "1") {
        await this.$router.push({
          path: "/orders/all",
          query: { page: 1 },
        });
      }
      this.setTableRows();
    },
    async resetFilter() {
      this.filterOptions.orderNum = null;
      this.filterOptions.userName = null;
      this.filterOptions.clientNumber = null;
      this.filterOptions.region = null;
      this.filterOptions.city = null;
      this.filterOptions.district = null;
      this.filterOptions.status = null;
      this.filterOptions.from_date = null;
      this.filterOptions.to_date = null;
      if (this.$route.query.page !== "1") {
        await this.$router.push({
          path: "/orders/all",
          query: { page: 1 },
        });
      }
      this.setTableRows();
    },
    // End:: Handel Filter

    // Start:: Set Table Rows
    updateRouterQueryParam(pagenationValue) {
      this.$router.push({
        query: {
          ...this.$route.query,
          page: pagenationValue,
        },
      });

      // Scroll To Screen's Top After Get Products
      document.body.scrollTop = 0; // For Safari
      document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
    },
    async setTableRows() {
      this.loading = true;
      try {
        // Set up params according to BE filtering expectations (with filter[])
        const params = {
          page: this.paginations.current_page,
        };
        if (this.filterOptions.orderNum) {
          params["filter[order_number]"] = this.filterOptions.orderNum;
        }
        if (this.filterOptions.userName) {
          params["filter[user_name]"] = this.filterOptions.userName;
        }
        if (this.filterOptions.clientNumber) {
          params["filter[client_number]"] = this.filterOptions.clientNumber;
        }
        if (this.filterOptions.city) {
          params["filter[city_id]"] = this.filterOptions.city.id;
        }
        if (this.filterOptions.district) {
          params["filter[district_id]"] = this.filterOptions.district.id;
        }
        if (this.filterOptions.status?.value) {
          params["filter[status]"] = this.filterOptions.status.value;
        }
        if (this.filterOptions.from_date) {
          params["filter[date_from]"] = this.filterOptions.from_date;
        }
        if (this.filterOptions.to_date) {
          params["filter[date_to]"] = this.filterOptions.to_date;
        }

        let res = await this.$axios({
          method: "GET",
          url: "orders?per_page=10",
          params: params,
        });
        this.loading = false;

        // Map API response to table format
        this.tableRows = res.data.data?.map((item) => ({
          ...item,
          user_name: item.user?.name || null,
        }));

        this.paginations.last_page = res.data.pagination.last_page;
        this.paginations.items_per_page = res.data.pagination.per_page;
        this.paginations.current_page = res.data.pagination.current_page;
      } catch (error) {
        this.loading = false;
        console.log(error.response?.data?.message || error.message);
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.error")
        );
      }
    },
    // End:: Set Table Rows

    // Start:: Control Modals
    showReplayModal(replay) {
      this.dialogReplay = true;
      this.selectedReplayTextToShow = replay;
    },
    // End:: Control Modals

    // ==================== Start:: Crud ====================
    // ===== Start:: Show
    showItem(item) {
      this.$router.push({ path: `/orders/show/${item.id}` });
    },
    // ===== End:: Show

    // ==================== End:: Crud ====================

    // Start:: Fetch Regions, Cities & Districts
    async getRegions() {
      try {
        const res = await this.$axios({
          method: "GET",
          url: `regions?paginate=false&filter[is_active]=true`,
        });
        this.regions = res.data.data;
      } catch (error) {
        console.log(error.response?.data?.message || error.message);
      }
    },

    async getCitiesByRegion(regionId) {
      try {
        const res = await this.$axios({
          method: "GET",
          url: `cities`,
          params: {
            paginate: false,
            "filter[is_active]": true,
            "filter[region_id]": regionId,
          },
        });
        this.cities = res.data.data;
      } catch (error) {
        console.log(error.response?.data?.message || error.message);
      }
    },

    async getDistrictsByCity(cityId) {
      try {
        const res = await this.$axios({
          method: "GET",
          url: `districts`,
          params: {
            paginate: false,
            "filter[is_active]": true,
            "filter[city_id]": cityId,
          },
        });
        this.districts = res.data.data;
      } catch (error) {
        console.log(error.response?.data?.message || error.message);
      }
    },
    // End:: Fetch Regions, Cities & Districts
  },

  created() {
    const year = this.today.getFullYear();
    const month = String(this.today.getMonth() + 1).padStart(2, "0");
    const day = String(this.today.getDate()).padStart(2, "0");
    const formattedDate = `${year}-${month}-${day}`;
    this.today = formattedDate;

    // Start:: Fire Methods
    window.addEventListener("click", () => {
      this.filterFormIsActive = false;
    });
    if (this.$route.query.page) {
      this.paginations.current_page = +this.$route.query.page;
    }
    this.setTableRows();
    this.getRegions();
    // End:: Fire Methods
  },
};
</script>
<style>
span.submit_btn {
  width: 45px;
  height: 45px;
  font-size: 16px;
  border-radius: 10px;
  color: var(--white_clr);
  transition: all 0.3s linear;
  background-color: #f6a513;
  display: flex;
  justify-content: center;
  align-items: center;
}

.actions button.btn_accept svg,
.actions button.btn_accept i {
  color: #4caf50;
}

.actions button.btn_processing svg,
.actions button.btn_processing i {
  color: #ff9800;
}

.actions button.btn_reject svg,
.actions button.btn_reject i {
  color: #f44336;
}

.actions button.btn_change_status svg,
.actions button.btn_change_status i {
  color: #2196f3;
}

.actions button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
